<template>
  <td class="text-end" :class="{ 'cell-changed': changed, 'cell-editable': editable, 'cell-locked': ! editable, 'cell-plain': plain }" :title="hint" @click="startEdit">
    <span class="cell-field">
      <span class="cell-text" :class="{ 'cell-text-hidden': isEditing }">{{ text }}</span>
      <input
          v-if="isEditing"
          ref="input"
          v-model="draft"
          type="text"
          inputmode="decimal"
          class="cell-input"
          @keyup.enter="commit"
          @keyup.esc="cancel"
          @blur="commit"
          @click.stop
      >
    </span>
    <slot></slot>
  </td>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import figureFormatter from '@/services/utils/figureFormatter'

const props = defineProps({
  value: { type: Number, default: 0 },
  changed: { type: Boolean, default: false },
  editable: { type: Boolean, default: false },
  busy: { type: Boolean, default: false },
  plain: { type: Boolean, default: false },
  title: { type: String, default: '' }
})

const emit = defineEmits(['onCommit'])

const { commaFormat } = figureFormatter()

const input = ref(null)
const isEditing = ref(false)
const draft = ref('')
const initial = ref('')
const typed = ref(null)

const text = computed(() => {
  if (typed.value !== null) return typed.value
  if (! props.value) return ''

  return commaFormat(props.value)
})

const hint = computed(() => {
  if (props.title) return props.title
  if (props.editable) return 'Click to change'

  return ''
})

const startEdit = async () => {
  if (! props.editable || isEditing.value) return

  initial.value = props.value ? String(props.value) : ''
  draft.value = initial.value
  isEditing.value = true
  await nextTick()

  if (! input.value) return

  input.value.focus()
  input.value.select()
}

const cancel = () => {
  isEditing.value = false
}

const commit = () => {
  if (! isEditing.value) return

  isEditing.value = false

  const value = draft.value.trim()

  if (value === initial.value) return

  typed.value = value
  emit('onCommit', value)
}

watch(() => props.busy, (busy) => {
  if (busy) return

  typed.value = null
})

watch(() => props.value, () => {
  typed.value = null
})
</script>

<style scoped>
.cell-field {
  position: relative;
  display: block;
}

.cell-text {
  display: block;
  min-height: 1.5rem;
}

.cell-text-hidden {
  visibility: hidden;
}

.cell-editable {
  cursor: text;
}

.cell-editable .cell-field {
  max-width: 9rem;
  margin-left: auto;
}

.cell-editable .cell-text {
  min-width: 5.5rem;
  padding: .2rem .6rem;
  border: 1px solid #f0dc8c;
  border-radius: 8px;
  background: #fff9db;
  color: #212529;
  transition: border-color .15s ease, box-shadow .15s ease;
}

.cell-editable.cell-plain .cell-text {
  min-width: 5rem;
  padding: 0 .35rem;
  border-color: #cfe0fb;
  background: transparent;
  color: inherit;
}

.cell-editable:hover .cell-text {
  border-color: #7367f0;
  box-shadow: 0 0 0 3px rgba(115, 103, 240, .12);
}

.cell-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: .2rem .6rem;
  border: 1px solid #7367f0;
  border-radius: 8px;
  outline: none;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(115, 103, 240, .18);
  text-align: right;
  font: inherit;
  font-weight: 400;
}
</style>
